import { EventListeners, MinzeElement } from 'minze'
import {
  LvcActivityPackSubscriber,
  PrebuiltPacks,
} from '../../components/activity-pack/lvc-activity-pack'
import {
  fetchPackItems,
  removeDuplicatesAndFilterCancelledSoldOut,
} from '../../components/activity-pack/utils'
import { config } from '../../constants'
import { Locale } from '../../modules/locale'
import MainModule from '../../modules/main'
import { TabItem } from '../lvc-tabs'

export interface LvcHomeActivityPackData {
  title?: { [key: string]: string }
  tabIcon?: string
  isEmojiLikeVariant?: boolean
  description?: string
  descriptionBanner?: {
    desktop: string
    mobile: string
  }
  dataPackConfig?: string
  prebuiltTag?: PrebuiltPacks
  displayOnlyForUserType?: 'authenticated' | 'not-authenticated' | null
  // New backend caching properties
  isCached?: boolean
  eventData?: Array<any | null>
  eventCountFromConfig?: number
  eventCountFetched?: number
}
export interface LvcHomeActivityPackWrapper {
  data: TabItem[]
  loading: boolean
  packList: string[]
}
export class LvcHomeActivityPackWrapper extends MinzeElement {
  data = [] as TabItem[]
  activityPackList = [] as LvcHomeActivityPackData[]
  userRadius: null | number = null
  loading = true
  algoliaFetchingInProgress = false

  // Instance management for subscribers
  private subscribers: Map<string, LvcActivityPackSubscriber> = new Map()
  private instanceId: string
  private isDestroyed = false

  constructor() {
    super()
    this.instanceId = this.generateInstanceId()
  }

  /**
   * Generate a unique instance ID using crypto.randomUUID() with fallback
   */
  private generateInstanceId(): string {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) {
      return crypto.randomUUID()
    }
    // Fallback for environments without crypto.randomUUID
    return 'home-wrapper-' + Math.random().toString(36).substr(2, 9) + '-' + Date.now().toString(36)
  }

  /**
   * Get or create a subscriber for a specific pack
   */
  private getSubscriberForPack(packIndex: number): LvcActivityPackSubscriber {
    const subscriberKey = `pack-${packIndex}`

    if (!this.subscribers.has(subscriberKey)) {
      const groupId = `home-activity-pack-${this.instanceId}-${subscriberKey}`
      const subscriber = new LvcActivityPackSubscriber(groupId)
      MainModule.getInstance().getSearchManager().subscribe(subscriber)
      this.subscribers.set(subscriberKey, subscriber)
    }

    return this.subscribers.get(subscriberKey)!
  }

  /**
   * Clean up all subscribers
   */
  private cleanupSubscribers(): void {
    for (const [key, subscriber] of this.subscribers) {
      try {
        MainModule.getInstance().getSearchManager().unsubscribe(subscriber)
      } catch (error) {
        console.warn(`Error unsubscribing subscriber ${key}:`, error)
      }
    }
    this.subscribers.clear()
  }

  /**
   * Process cached event data from manual activity packs
   * Filters out null entries and maintains proper data structure
   */
  private processCachedEventData(item: LvcHomeActivityPackData): {
    items: any[]
    total: number
    disableViewMore: boolean
    disableLimit: boolean
  } {
    if (!item.isCached || !item.eventData) {
      throw new Error('Item is not cached or missing eventData')
    }

    // Filter out null entries and remove duplicates/cancelled/sold out
    const validEvents =
      removeDuplicatesAndFilterCancelledSoldOut(item.eventData.filter(Boolean)) || []

    return {
      items: validEvents || [],
      total: validEvents?.length || 0,
      disableViewMore: true, // Manual packs disable view more
      disableLimit: true, // Manual packs disable limit
    }
  }
  fetchActivityPackList = async () => {
    const homepageApi = `${config.backendApi}/content/getHomepage`

    const response = await fetch(homepageApi)
    const respData = await response.json()
    const activityPackList = respData?.activityPackList?.[
      Locale.language
    ] as LvcHomeActivityPackData[]
    return activityPackList
  }
  onStart = () => {
    MainModule.getInstance().getEE().on('geo:location', this.handleStateChange)
    MainModule.getInstance().getEE().on('lvc:user_state_changed', this.handleStateChange)
  }

  onDestroy = () => {
    this.isDestroyed = true

    // Clean up all subscribers and search cores
    this.cleanupSubscribers()

    // Remove event listeners
    MainModule.getInstance().getEE().off('geo:location', this.handleStateChange)
    MainModule.getInstance().getEE().off('lvc:user_state_changed', this.handleStateChange)
  }
  fetchData = async (location: { latitude: number; longitude: number }, isLoggedIn: boolean) => {
    const DEFAULT_LIMIT = 14
    const MAX_LIMIT = 50
    // Instance validation
    if (this.isDestroyed) {
      console.warn('Component destroyed, skipping fetchData')
      return []
    }

    let packData = []
    let packIndex = 0

    for (const item of this.activityPackList) {
      // if user is not logged in and the pack is recommended or only for authenticated users, skip
      if (
        !isLoggedIn &&
        (item.prebuiltTag === 'recommended' || item.displayOnlyForUserType === 'authenticated')
      ) {
        continue
        // if user is logged in and the pack is only for not authenticated users, skip
      } else if (isLoggedIn && item.displayOnlyForUserType === 'not-authenticated') {
        continue
        // all other cases
      } else {
        // Instance validation before processing each pack
        if (this.isDestroyed) {
          console.warn('Component destroyed during fetchData, stopping')
          break
        }

        let data: any = null

        // Check if this is a cached manual activity pack
        if (item.isCached && item.eventData) {
          try {
            // Use pre-fetched data for manual packs - no API call needed!
            data = this.processCachedEventData(item)
          } catch (error) {
            console.warn('Error processing cached data, falling back to Algolia:', error)
            // Fall back to normal Algolia processing if cached data fails
            data = null
          }
        }

        // If not cached or cached processing failed, use normal Algolia flow
        if (!data) {
          // parse the data pack config if it exists
          let decodedDataPackConfig
          if (item.dataPackConfig) {
            decodedDataPackConfig = JSON.parse(decodeURIComponent(item.dataPackConfig))
          }
          // if the dataPackConfig does not have a radius and the user has a preferred radius, set it
          if (decodedDataPackConfig && !decodedDataPackConfig?.radius && this.userRadius) {
            decodedDataPackConfig.radius = this.userRadius
          }

          // Use instance-specific subscriber for this pack
          const subscriber = this.getSubscriberForPack(packIndex)
          data = await fetchPackItems(
            subscriber,
            decodedDataPackConfig ? decodedDataPackConfig : null,
            item?.prebuiltTag,
            location
          )
        }

        // Final validation before adding to results
        if (this.isDestroyed) {
          console.warn('Component destroyed during fetchData, discarding results')
          break
        }

        // Additional validation for cached data - fallback to Algolia if no valid events
        if (item.isCached && data && data.items && data.items.length === 0) {
          console.warn(
            `No valid events in cached data for pack "${item.title?.en || item.title?.fr || 'Unknown'}", attempting Algolia fallback`
          )

          try {
            // parse the data pack config for fallback
            let decodedDataPackConfig
            if (item.dataPackConfig) {
              decodedDataPackConfig = JSON.parse(decodeURIComponent(item.dataPackConfig))
            }
            if (decodedDataPackConfig && !decodedDataPackConfig?.radius && this.userRadius) {
              decodedDataPackConfig.radius = this.userRadius
            }

            // Use instance-specific subscriber for fallback
            const subscriber = this.getSubscriberForPack(packIndex)
            const fallbackData = await fetchPackItems(
              subscriber,
              decodedDataPackConfig ? decodedDataPackConfig : null,
              item?.prebuiltTag,
              location
            )

            if (fallbackData && fallbackData.items && fallbackData.items.length > 0) {
              data = fallbackData
            }
          } catch (fallbackError) {
            console.error('Fallback to Algolia also failed:', fallbackError)
          }
        }

        const items = data?.items ?? []
        const title = item?.title?.[Locale.language as 'fr' | 'en'] || data?.title || ''
        const tabIcon = item?.tabIcon || ''
        const isEmojiLikeVariant = item?.isEmojiLikeVariant || false
        const descriptionBanner = item?.descriptionBanner || ''

        let limit = DEFAULT_LIMIT
        if (item.dataPackConfig) {
          try {
            const decodedConfig = JSON.parse(decodeURIComponent(item.dataPackConfig))
            const isManualPack = decodedConfig?.eventList?.length > 0
            const isCachedPack = item.isCached && data?.disableLimit
            limit =
              isManualPack || isCachedPack
                ? items.length || MAX_LIMIT
                : decodedConfig?.limit || DEFAULT_LIMIT // Use actual item count or high number to bypass limit
          } catch (error) {
            console.warn('Error parsing dataPackConfig for limit:', error)
          }
        }

        packData.push({
          title: title,
          tabIcon: tabIcon,
          isEmojiLikeVariant: isEmojiLikeVariant,
          tabContent: `
          <lvc-activity-pack-layout
            data-component-location="accueil"
            title="${title}"
            tab-icon="${tabIcon}"
            is-emoji-like-variant="${isEmojiLikeVariant}"
            description="${item?.description ?? null}"
            description-banner="${descriptionBanner ? encodeURIComponent(JSON.stringify(descriptionBanner)) : null}"
            items="${encodeURIComponent(JSON.stringify(items))}"
            total="${data?.total || 0}"
            search-config="${data?.searchConfig ? encodeURIComponent(JSON.stringify(data.searchConfig)) : null}"
            disable-view-more="${data?.disableViewMore || false}"
            disable-limit="${data?.disableLimit || false}"
            limit="${limit}"
            >
          </lvc-activity-pack-layout>
          `,
          total: data?.total || 0,
        })

        packIndex++
      }
    }
    return packData
  }

  handleStateChange = async () => {
    if (this.algoliaFetchingInProgress) return
    this.algoliaFetchingInProgress = true
    const isLoggedIn = MainModule.getInstance().getAuth().isLoggedIn()
    const location = MainModule.getInstance().getGeo().currentLocation

    // get the user's preferred radius from the user's profile
    const userData = MainModule.getInstance().getAuth().getUserData()

    // if the user has a preferred radius, use it
    if (userData?.profile?.personalData?.radius) {
      this.userRadius = userData.profile.personalData.radius
    }

    this.loading = true
    if (!this.activityPackList || this.activityPackList.length === 0) {
      this.activityPackList = await this.fetchActivityPackList()
    }
    this.data = await this.fetchData(location, isLoggedIn)
    this.loading = false
    this.algoliaFetchingInProgress = false
    this.rerender(true)
  }
  html = () => `
    <div class="activity-pack-wrapper">
        <lvc-tab-group-loader class="${this.loading ? '' : 'loading'}" global-padding="false"></lvc-tab-group-loader>
        ${this.data && !this.loading ? `<lvc-tab-group class="${this.loading ? 'loading' : ''}" global-padding="false" geoloc-button="true" navigation="true" items="${encodeURIComponent(JSON.stringify(this.data))}"></lvc-tab-group>` : ''}
    </div>
  `

  css = () => `
    :host {
      padding-left: 6rem;
      padding-right: 6rem;
    }
    .activity-pack-wrapper {
      width: 100%;
      max-width: 80rem;
      margin-left: auto;
      margin-right: auto;
    }
    .container-large {
      width: 100%;
      max-width: 80rem;
      margin-left: auto!important;
      margin-right: auto!important;
    }
    .loading {
      display: none;
    }
    @media screen and (max-width: 991px) {
      :host {
        padding-left: 4.5rem;
        padding-right: 4.5rem;
      }
    }
    @media screen and (max-width: 767px) {
      :host {
        padding-left: 0rem;
        padding-right: 0rem;
      }
    }
  `

  eventListeners: EventListeners = [
    //[window, 'lvc:user_state_changed', this.handleStateChange],
  ]
}
